// The ground behind a screen should be that screen's own background colour:
// white behind the splash, teal behind the letter hook, the sheet grey behind
// the dashboard. Rather than keep a per-screen table, sample the colour the
// screen actually paints at the top-left corner of the canvas, and paint the
// ground (and the document body, for overscroll) to match.
//
// Sampling re-runs on navigation, on resize, and whenever the canvas subtree
// changes (a MutationObserver, debounced), because many screens mount their
// real content only after a data load and the navigator's placeholder card
// is what paints first. Images and transparent wrappers are skipped, so an
// ornament or a back arrow at that corner does not confuse the sample.

import { useEffect, useState } from 'react';

const TRANSPARENT = /^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\)$|^transparent$/;

export function sampleGround(canvas: HTMLElement | null): string | null {
  if (!canvas || typeof document === 'undefined') return null;
  const r = canvas.getBoundingClientRect();
  if (r.width === 0) return null;
  const x = Math.min(Math.max(r.left, 0) + 2, window.innerWidth - 1);
  const y = Math.min(Math.max(r.top, 0) + 2, window.innerHeight - 1);
  for (const el of document.elementsFromPoint(x, y)) {
    if (el === canvas) break;
    if (!(el instanceof HTMLElement) || el.tagName === 'IMG') continue;
    // Overlays rendered outside the canvas (Alert host, modals, the Orion
    // button layer) are not the screen; look through them.
    if (!canvas.contains(el)) continue;
    const bg = getComputedStyle(el).backgroundColor;
    if (bg && !TRANSPARENT.test(bg)) return bg;
  }
  return null;
}

/**
 * Colour of the current screen's background. `routeKey` restarts sampling
 * on navigation; `fallback` is used until a sample succeeds.
 */
export function useScreenGround(
  canvasRef: React.RefObject<HTMLElement | null>,
  routeKey: string,
  fallback: string,
): string {
  const [ground, setGround] = useState(fallback);

  useEffect(() => {
    let cancelled = false;
    let debounce: ReturnType<typeof setTimeout> | undefined;

    const apply = () => {
      if (cancelled) return;
      const color = sampleGround(canvasRef.current);
      if (color) setGround((prev) => (prev === color ? prev : color));
    };
    const applySoon = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(apply, 80);
    };

    apply();
    const timers = [120, 450, 1200, 3000].map((ms) => setTimeout(apply, ms));
    window.addEventListener('resize', applySoon);

    // Content that arrives later (after a fetch, after a transition) changes
    // the DOM under the canvas; sample again when it does.
    const observer =
      typeof MutationObserver !== 'undefined' && canvasRef.current
        ? new MutationObserver(applySoon)
        : null;
    observer?.observe(canvasRef.current as HTMLElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (debounce) clearTimeout(debounce);
      window.removeEventListener('resize', applySoon);
      observer?.disconnect();
    };
  }, [canvasRef, routeKey]);

  useEffect(() => {
    document.body.style.backgroundColor = ground;
  }, [ground]);

  return ground;
}
